import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
    applyColgroupWidths,
    Indent,
    LineHeight, NumberUtils, OrderedTextAlign,
    PageBreak,
    PlaceholderMention,
    RichImage,
    TableCellStyle,
    TableStyle,
    TabSpan, USER_INPUT_DEBOUNCE_TIME
} from '@modeso/aumico-lib-core-fe';
import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';
import {
    IAccount,
    IAccountMappingTemplate,
    IAopCcr,
    IAppropriationOfProfitEntry,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { AnyExtension } from '@tiptap/core';
import { Editor } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../constants/localizationKeys';

const debug = Debug('aumico:aumico-lib-annualreport-fe:CcrComponent');

/** Extension of IAopCcr that carries the manually entered previous-year balance
 *  values when no equity-account data is available. These extra fields are
 *  transparent to the backend (stored/returned as raw JSON) and let us
 *  restore the user's input on reload. */
type IAopCcrPersisted = IAopCcr & {
    manualPreviousOpeningBalance?: number | null;
    manualPreviousClosingBalance?: number | null;
};

interface ICcrEntryRow {
    name: string | null;
    currentValue: number | null;
    previousValue: number | null;
    /** True only for rows that were rolled forward from the previous year's CCR.
     *  Manually added rows must never be locked, even when they have a previousValue. */
    fromPreviousYear?: boolean;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'aumico-annualReport-ccr-component',
    templateUrl: 'ccr.component.html',
    styleUrls: ['ccr.component.scss'],
})
export class CcrComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    // ─── Inputs ────────────────────────────────────────────────────────────────

    @Input() currentYearDate: string;
    @Input() previousYearDate: string;
    @Input() currency: string;
    @Input() numberFormat = '1.2-2';
    @Input() savedCcr: IAopCcr | undefined;
    @Input() previousCcr: IAopCcr | undefined;
    @Input() equityAccounts: IAccount[] = [];
    @Input() balanceTemplate: IAccountMappingTemplate;
    @Input() reportLocale: string;
    @Input() isSavedBefore = false;

    // ─── Outputs ───────────────────────────────────────────────────────────────

    @Output() ccrOutput = new EventEmitter<IAopCcr>();
    @Output() ccrPrefilledValue = new EventEmitter<IAopCcr>();

    // ─── Tiptap footnote editor ────────────────────────────────────────────────

    @ViewChild('ccrFootnoteHost') ccrFootnoteHost?: ElementRef<HTMLDivElement>;
    tiptapEditor: Editor | null = null;

    // ─── Public state ──────────────────────────────────────────────────────────

    form: UntypedFormGroup;
    entriesArray: UntypedFormArray;

    /** Account selector (not part of entriesArray) */
    equityAccountControl = new UntypedFormControl([]);

    /** Manual overrides for the previous-year system rows when no previous-year
     *  data is available from the equity account or prior CCR. */
    openingPrevControl = new UntypedFormControl(null);
    closingPrevControl = new UntypedFormControl(null);

    /** Standalone control for the footnote checkbox.
     *  Using a FormControl lets us call setValue() to force writeValue() on the
     *  directive, bypassing the @Input binding-equality check that prevents snap-back. */
    footnoteCheckboxControl = new UntypedFormControl(false);

    ccrDescription: string;
    totalBeginningText: string;
    totalEndText: string;
    showFootnote = false;
    ccrFootnote = '';
    showRemoveFootnoteConfirm = false;

    selectedEquityAccounts: IAccount[] = [];

    /** Computed balances based on selected equity accounts (aggregated sum) */
    openingCur: number | null = null;
    closingCur: number | null = null;
    openingPrev: number | null = null;
    closingPrev: number | null = null;

    /** Diff warnings */
    priorYearDiff: number | null = null;
    currentYearDiff: number | null = null;

    entriesData: ICcrEntryRow[] = [];
    /** Stable references rebuilt only when equityAccounts input changes.
     *  hamSimpleDropDown compares option objects by reference, so the control
     *  values must point to objects in this exact array to show the selections. */
    equityAccountsForDropdown: Array<IAccount & { displayLabel: string }> = [];
    private outputData: IAopCcr | undefined;
    private formChanges: Subscription | undefined;
    private accountChanges: Subscription | undefined;
    private initializationDone = false;

    private readonly local = localizationKeys;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private i18next: I18NextService,
        private cdr: ChangeDetectorRef,
    ) {
        this.initForm();
    }

    // ─── Lifecycle ─────────────────────────────────────────────────────────────

    ngOnInit(): void {
        this.applyDefaultTexts();
        this.listenToAccountControl();
        this.listenToManualPrevControls();
    }

    ngAfterViewInit(): void {
        // Tiptap is lazily mounted when showFootnote becomes true
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Always keep the dropdown option list in sync with the equityAccounts input.
        if (changes['equityAccounts']) {
            this.rebuildDropdownAccounts();
        }

        if (this.initializationDone) {
            // If equityAccounts input updated after initialization, re-point each selected
            // account to its refreshed dropdown object so the display is correct.
            if (changes['equityAccounts'] && this.selectedEquityAccounts.length > 0) {
                const refreshed = this.selectedEquityAccounts
                    .map((a) => this.findDropdownOption(a.accountNumber))
                    .filter((o): o is IAccount & { displayLabel: string } => o != null);
                this.equityAccountControl.setValue(refreshed, { emitEvent: false });
                this.cdr.markForCheck();
            }
            return;
        }

        if (changes['savedCcr'] && this.savedCcr != null) {
            this.fillFromSavedCcr();
            this.initializationDone = true;
            this.outputData = cloneDeep(this.buildOutput());
            this.ccrPrefilledValue.emit(this.outputData);
            return;
        }

        if (changes['previousCcr'] && this.previousCcr != null) {
            this.fillFromPreviousCcr();
            this.initializationDone = true;
            this.outputData = cloneDeep(this.buildOutput());
            this.ccrPrefilledValue.emit(this.outputData);
            return;
        }

        // emit defaults even without any autofill data
        if ((changes['equityAccounts'] || changes['reportLocale']) && !this.initializationDone) {
            this.applyDefaultTexts();
            this.outputData = cloneDeep(this.buildOutput());
            this.ccrPrefilledValue.emit(this.outputData);
        }
    }

    ngOnDestroy(): void {
        this.destroyTiptap();
    }

    // ─── Account selection ─────────────────────────────────────────────────────

    private rebuildDropdownAccounts(): void {
        this.equityAccountsForDropdown = (this.equityAccounts ?? [])
            .filter((a) => !!a.accountNumber)
            .map((a) => ({
                ...a,
                displayLabel: `${a.accountNumber} – ${a.name}`,
            }));
    }

    /** Find the dropdown object whose accountNumber matches. Used when setting the
     *  control value so that hamSimpleDropDown can match by reference. */
    private findDropdownOption(accountNumber: string | undefined): (IAccount & { displayLabel: string }) | null {
        if (!accountNumber) return null;
        return this.equityAccountsForDropdown.find((a) => a.accountNumber === accountNumber) ?? null;
    }

    private listenToManualPrevControls(): void {
        this.openingPrevControl.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), distinctUntilChanged(), untilDestroyed(this))
            .subscribe(() => {
                this.recalcDiffs();
                this.emitOutput();
                this.cdr.markForCheck();
            });
        this.closingPrevControl.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), distinctUntilChanged(), untilDestroyed(this))
            .subscribe(() => {
                this.recalcDiffs();
                this.emitOutput();
                this.cdr.markForCheck();
            });
    }

    private listenToAccountControl(): void {
        if (this.accountChanges) {
            this.accountChanges.unsubscribe();
        }
        this.accountChanges = this.equityAccountControl.valueChanges
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((accounts: IAccount[]) => {
                this.onEquityAccountsSelected(accounts);
            });
    }

    private onEquityAccountsSelected(accounts: IAccount[]): void {
        // The dropdown emits objects from equityAccountsForDropdown (spread copies).
        // Resolve each back to the original IAccount from equityAccounts so that any
        // class-instance getters (calculatedOpeningBalance, etc.) are accessible.
        const selected = (accounts ?? []).map(
            (a) => this.equityAccounts?.find((orig) => orig.accountNumber === a.accountNumber) ?? a,
        );
        this.selectedEquityAccounts = selected;
        this.refreshBalanceValues();
        this.recalcDiffs();
        this.emitOutput();
        this.cdr.markForCheck();
    }

    private refreshBalanceValues(): void {
        // REV-20899: the displayed CCR balance must include each account's
        // transactional reclass. `closingReClass` / `openingReClass` are
        // populated by the provider's `applyReclassificationsToFinStatements`
        // helper before the accounts ever reach this component.
        if (this.selectedEquityAccounts.length > 0) {
            this.openingCur = CcrComponent.sumFinalBalances(
                this.selectedEquityAccounts.map((a) => ({ balance: a.calculatedOpeningBalance, reclass: a.openingReClass })),
            );
            this.closingCur = CcrComponent.sumFinalBalances(
                this.selectedEquityAccounts.map((a) => ({ balance: a.calculatedClosingBalance, reclass: a.closingReClass })),
            );
            // closing prev = current year opening balance (= end of previous year)
            this.closingPrev = this.openingCur;
        } else {
            this.openingCur = null;
            this.closingCur = null;
            this.closingPrev = null;
        }
        // opening previous = sum of previous year's account opening balances
        const prevAccounts = this.resolvedPreviousCcrAccounts();
        this.openingPrev = prevAccounts.length > 0
            ? CcrComponent.sumFinalBalances(
                prevAccounts.map((a) => ({ balance: a.calculatedOpeningBalance, reclass: a.openingReClass })),
            )
            : null;
    }

    /** Returns the equity accounts from the previous CCR, normalising the legacy
     *  single-account field (`equityAccount`) to an array. */
    private resolvedPreviousCcrAccounts(): IAccount[] {
        if (!this.previousCcr) return [];
        if (this.previousCcr.equityAccounts?.length) return this.previousCcr.equityAccounts;
        if (this.previousCcr.equityAccount) return [this.previousCcr.equityAccount];
        return [];
    }

    /** Sums finalBalance() across a set of {balance, reclass} pairs.
     *  Returns null only when every pair resolves to null. */
    private static sumFinalBalances(pairs: Array<{ balance: number | undefined | null; reclass: number | undefined | null }>): number | null {
        const values = pairs.map((p) => CcrComponent.finalBalance(p.balance, p.reclass));
        if (values.every((v) => v == null)) return null;
        return NumberUtils.roundNumber(values.reduce<number>((sum, v) => sum + (v ?? 0), 0));
    }

    /** REV-20899: helper for combining the imported balance with the transactional reclass. */
    private static finalBalance(balance: number | undefined | null, reclass: number | undefined | null): number | null {
        const b = balance != null && NumberUtils.isFinite(balance) ? balance : null;
        const r = reclass != null && NumberUtils.isFinite(reclass) ? reclass : 0;
        if (b == null && r === 0) return null;
        return NumberUtils.roundNumber((b ?? 0) + r);
    }

    // ─── Form management ───────────────────────────────────────────────────────

    private initForm(): void {
        this.form = this.formBuilder.group({ entries: this.formBuilder.array([]) });
        this.entriesArray = this.form.get('entries') as UntypedFormArray;
        this.subscribeToFormChanges();
    }

    private subscribeToFormChanges(): void {
        if (this.formChanges) {
            this.formChanges.unsubscribe();
        }
        this.formChanges = this.form.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((a, b) => fastDeepEqual(a, b)),
                untilDestroyed(this),
            )
            .subscribe(() => this.onFormChange());
    }

    private onFormChange(): void {
        // Sync only the value fields from the form; preserve metadata like fromPreviousYear.
        const formValues = this.entriesArray.value as ICcrEntryRow[];
        formValues.forEach((v, i) => {
            if (this.entriesData[i]) {
                this.entriesData[i].name = v.name;
                this.entriesData[i].currentValue = v.currentValue;
                this.entriesData[i].previousValue = v.previousValue;
            }
        });
        this.recalcDiffs();
        this.outputData = cloneDeep(this.buildOutput());
        debug('ccrOutput', this.outputData);
        this.ccrOutput.emit(this.outputData);
        this.cdr.markForCheck();
    }

    private rebuildEntriesArray(): void {
        while (this.entriesArray.length) {
            this.entriesArray.removeAt(0);
        }
        for (const entry of this.entriesData) {
            this.entriesArray.push(this.buildEntryFormGroup(entry));
        }
        this.subscribeToFormChanges();
    }

    private buildEntryFormGroup(entry: ICcrEntryRow): UntypedFormGroup {
        return this.formBuilder.group({
            name: new UntypedFormControl(entry.name),
            currentValue: new UntypedFormControl(entry.currentValue),
            previousValue: new UntypedFormControl(entry.previousValue),
        });
    }

    addRow(): void {
        const newEntry: ICcrEntryRow = { name: null, currentValue: null, previousValue: null };
        this.entriesData.push(newEntry);
        this.entriesArray.push(this.buildEntryFormGroup(newEntry));
        this.recalcDiffs();
        this.emitOutput();
        this.cdr.markForCheck();
    }

    removeAt(index: number): void {
        const entry = this.entriesData[index];
        // Cannot delete rows that were rolled forward from the previous year
        if (entry?.fromPreviousYear === true) {
            return;
        }
        this.entriesData.splice(index, 1);
        this.entriesArray.removeAt(index);
        this.recalcDiffs();
        this.emitOutput();
        this.cdr.markForCheck();
    }

    isDeleteDisabled(entry: ICcrEntryRow): boolean {
        return entry?.fromPreviousYear === true;
    }

    isLabelLocked(entry: ICcrEntryRow): boolean {
        return entry?.fromPreviousYear === true;
    }

    getEntryControl(prop: string, index: number): UntypedFormControl {
        return (this.entriesArray.at(index) as UntypedFormGroup).get(prop) as UntypedFormControl;
    }

    // ─── Diff calculations ─────────────────────────────────────────────────────

    private recalcDiffs(): void {
        const rows: ICcrEntryRow[] = this.entriesArray.value ?? this.entriesData;

        // Prior year: openingPrev + sum(previousValues) should equal closingPrev
        const effectiveOpeningPrev = this.openingPrev ?? (this.openingPrevControl.value != null ? +this.openingPrevControl.value : null);
        const effectiveClosingPrev = this.closingPrev ?? (this.closingPrevControl.value != null ? +this.closingPrevControl.value : null);
        if (effectiveOpeningPrev != null && effectiveClosingPrev != null) {
            const sumPrev = NumberUtils.sumIgnoringInfiniteNumbers(
                rows.map((r) => r.previousValue),
                undefined,
            ) ?? 0;
            const expected = NumberUtils.sumIgnoringInfiniteNumbers([effectiveOpeningPrev, sumPrev], undefined) ?? 0;
            const diff = NumberUtils.subtractNumbers(effectiveClosingPrev, expected);
            this.priorYearDiff = diff !== 0 ? diff : null;
        } else {
            this.priorYearDiff = null;
        }

        // Current year: openingCur + sum(currentValues) should equal closingCur
        if (this.openingCur != null && this.closingCur != null) {
            const sumCur = NumberUtils.sumIgnoringInfiniteNumbers(
                rows.map((r) => r.currentValue),
                undefined,
            ) ?? 0;
            const expected = NumberUtils.sumIgnoringInfiniteNumbers([this.openingCur, sumCur], undefined) ?? 0;
            const diff = NumberUtils.subtractNumbers(this.closingCur, expected);
            this.currentYearDiff = diff !== 0 ? diff : null;
        } else {
            this.currentYearDiff = null;
        }
    }

    // ─── Inline texts ──────────────────────────────────────────────────────────

    private applyDefaultTexts(): void {
        if (!this.ccrDescription) {
            this.ccrDescription = this.i18next.t(this.local.aumico_annual_report_ccr_description, {
                lng: this.reportLocale,
            }) as string;
        }
        if (!this.totalBeginningText) {
            this.totalBeginningText = this.i18next.t(this.local.aumico_annual_report_ccr_beginning_of_year, {
                lng: this.reportLocale,
            }) as string;
        }
        if (!this.totalEndText) {
            this.totalEndText = this.i18next.t(this.local.aumico_annual_report_ccr_end_of_year, {
                lng: this.reportLocale,
            }) as string;
        }
    }

    onSaveCcrDescription(text: string): void {
        this.ccrDescription = text;
        this.emitOutput();
    }

    onSaveTotalBeginningText(text: string): void {
        this.totalBeginningText = text;
        this.emitOutput();
    }

    onSaveTotalEndText(text: string): void {
        this.totalEndText = text;
        this.emitOutput();
    }

    // ─── Footnote / Tiptap ─────────────────────────────────────────────────────

    onFootnoteCheckboxChange(checked: boolean): void {
        if (!checked && this.ccrFootnote) {
            // Has content — show the confirmation modal.
            // Snap the FormControl back to true immediately so writeValue(true) is called
            // on the directive, forcing this.checked = true before Angular runs CD.
            this.footnoteCheckboxControl.setValue(true, { emitEvent: false });
            this.showRemoveFootnoteConfirm = true;
            this.cdr.markForCheck();
        } else if (checked) {
            this.showFootnote = true;
            this.cdr.markForCheck();
            // Mount tiptap after Angular has rendered the host element
            setTimeout(() => this.ensureTiptapEditor(), 0);
        } else {
            // unchecked and no existing footnote content
            this.showFootnote = false;
            this.destroyTiptap();
            this.emitOutput();
            this.cdr.markForCheck();
        }
    }

    confirmRemoveFootnote(): void {
        this.showRemoveFootnoteConfirm = false;
        this.footnoteCheckboxControl.setValue(false, { emitEvent: false });
        this.ccrFootnote = '';
        this.showFootnote = false;
        this.destroyTiptap();
        this.emitOutput();
        this.cdr.markForCheck();
    }

    cancelRemoveFootnote(): void {
        this.showRemoveFootnoteConfirm = false;
        // footnoteCheckboxControl was already snapped back to true above
        this.showFootnote = true;
        this.cdr.markForCheck();
    }

    private ensureTiptapEditor(): void {
        if (this.tiptapEditor || !this.ccrFootnoteHost?.nativeElement) {
            return;
        }

        const extensions: AnyExtension[] = [
            StarterKit as unknown as AnyExtension,
            Underline as unknown as AnyExtension,
            OrderedTextAlign.configure({ types: ['heading', 'paragraph'] }) as unknown as AnyExtension,
            LineHeight,
            Indent,
            RichImage.configure({ inline: false }) as unknown as AnyExtension,
            PlaceholderMention.configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: this.createMentionSuggestion(),
            }) as unknown as AnyExtension,
            Table.configure({ resizable: true }) as unknown as AnyExtension,
            TableRow as unknown as AnyExtension,
            TableHeader as unknown as AnyExtension,
            TableCell as unknown as AnyExtension,
            PageBreak,
            TabSpan,
            TableStyle,
            TableCellStyle,
        ];

        this.tiptapEditor = new Editor({
            element: this.ccrFootnoteHost.nativeElement,
            extensions,
            content: applyColgroupWidths(this.ccrFootnote ?? ''),
            onUpdate: ({ editor }) => {
                this.ccrFootnote = editor.getHTML();
                this.emitOutput();
            },
        });
    }

    private createMentionSuggestion(): any {
        let popup: HTMLDivElement | null = null;
        let list: HTMLDivElement | null = null;
        let selectedIndex = 0;
        let currentItems: string[] = [];
        let currentCommand: ((payload: { id: string; label: string }) => void) | null = null;

        const updateList = (items: string[]) => {
            if (!list) { return; }
            list.innerHTML = '';
            items.forEach((item: string, index: number) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tiptap-mention-item';
                if (index === selectedIndex) { button.classList.add('is-selected'); }
                button.textContent = item;
                button.addEventListener('click', () => { currentCommand?.({ id: item, label: item }); });
                list?.appendChild(button);
            });
        };

        const updatePosition = (props: any) => {
            const rect = props.clientRect?.();
            if (!rect || !popup) { return; }
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
        };

        return {
            char: '@',
            items: ({ query }: { query: string }) =>
                SUPPORTED_PLACE_HOLDERS.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
            render: () => ({
                onStart: (props: any) => {
                    selectedIndex = 0;
                    popup = document.createElement('div');
                    popup.className = 'tiptap-mention-list';
                    list = document.createElement('div');
                    popup.appendChild(list);
                    document.body.appendChild(popup);
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : null;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onUpdate: (props: any) => {
                    selectedIndex = 0;
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : currentCommand;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onKeyDown: (props: any) => {
                    if (!list) { return false; }
                    if (currentItems.length === 0) { return false; }
                    if (props.event.key === 'ArrowDown') {
                        selectedIndex = (selectedIndex + 1) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'Enter') {
                        const item = currentItems[selectedIndex];
                        if (item && typeof currentCommand === 'function') {
                            currentCommand({ id: item, label: item });
                            return true;
                        }
                    }
                    if (props.event.key === 'Escape') { return true; }
                    return false;
                },
                onExit: () => {
                    popup?.remove();
                    popup = null;
                    list = null;
                    currentItems = [];
                    currentCommand = null;
                },
            }),
        };
    }

    private destroyTiptap(): void {
        this.tiptapEditor?.destroy();
        this.tiptapEditor = null;
    }

    // ─── Fill from saved / autofill ────────────────────────────────────────────

    private fillFromSavedCcr(): void {
        const s = this.savedCcr as IAopCcrPersisted;
        this.ccrDescription = s.ccrDescription ?? this.getDefaultDescription();
        this.totalBeginningText = s.totalCapitalContributionsAtTheBeginningOfTheYearText ?? this.getDefaultBeginningText();
        this.totalEndText = s.totalCapitalContributionsAtTheEndOfTheYearText ?? this.getDefaultEndText();
        this.ccrFootnote = s.ccrFootnote ?? '';
        this.showFootnote = !!this.ccrFootnote;
        this.footnoteCheckboxControl.setValue(this.showFootnote, { emitEvent: false });

        this.entriesData = (s.ccrEntries ?? []).map((e) => ({
            name: e.name,
            currentValue: e.currentValue != null && NumberUtils.isFinite(e.currentValue)
                ? NumberUtils.roundNumber(e.currentValue) : null,
            previousValue: e.previousValue != null && NumberUtils.isFinite(e.previousValue)
                ? NumberUtils.roundNumber(e.previousValue) : null,
            // Use the persisted flag; do NOT infer from previousValue — a user-added row
            // can also have a previousValue without being a rolled-forward row.
            fromPreviousYear: e.carriedForwardFromPreviousYear === true,
        }));
        this.rebuildEntriesArray();
        // Restore selected equity accounts.
        // Normalise legacy single-account field to an array, then map each to
        // a stable dropdown reference so hamSimpleDropDown can match by reference.
        const savedAccounts: IAccount[] = s.equityAccounts?.length
            ? s.equityAccounts
            : s.equityAccount ? [s.equityAccount] : [];
        const savedOptions = savedAccounts
            .map((a) => this.findDropdownOption(a.accountNumber))
            .filter((o): o is IAccount & { displayLabel: string } => o != null);
        if (savedOptions.length > 0) {
            this.equityAccountControl.setValue(savedOptions, { emitEvent: false });
            this.selectedEquityAccounts = savedOptions.map(
                (o) => this.equityAccounts?.find((a) => a.accountNumber === o.accountNumber) ?? o,
            );
        }

        this.refreshBalanceValues();

        // Restore manually entered previous-year balances (only when the account
        // data does not already supply them).
        if (this.openingPrev == null && s.manualPreviousOpeningBalance != null) {
            this.openingPrevControl.setValue(s.manualPreviousOpeningBalance, { emitEvent: false });
        }
        if (this.closingPrev == null && s.manualPreviousClosingBalance != null) {
            this.closingPrevControl.setValue(s.manualPreviousClosingBalance, { emitEvent: false });
        }

        this.recalcDiffs();
        this.cdr.markForCheck();

        if (this.showFootnote) {
            setTimeout(() => this.ensureTiptapEditor(), 0);
        }
    }

    private fillFromPreviousCcr(): void {
        const p = this.previousCcr;
        this.ccrDescription = p.ccrDescription ?? this.getDefaultDescription();
        this.totalBeginningText = p.totalCapitalContributionsAtTheBeginningOfTheYearText ?? this.getDefaultBeginningText();
        this.totalEndText = p.totalCapitalContributionsAtTheEndOfTheYearText ?? this.getDefaultEndText();
        this.ccrFootnote = p.ccrFootnote ?? '';
        this.showFootnote = !!this.ccrFootnote;

        this.footnoteCheckboxControl.setValue(this.showFootnote, { emitEvent: false });

        // Roll entries: previous year's currentValue becomes this year's previousValue.
        // Mark as fromPreviousYear so these rows stay locked/non-deletable.
        this.entriesData = (p.ccrEntries ?? []).map((e) => ({
            name: e.name,
            currentValue: null,
            previousValue: e.currentValue != null && NumberUtils.isFinite(e.currentValue)
                ? NumberUtils.roundNumber(e.currentValue) : null,
            fromPreviousYear: true,
        }));
        this.rebuildEntriesArray();

        // Restore equity accounts from the previous CCR, normalising the legacy
        // single-account field to an array.
        const prevAccounts: IAccount[] = p.equityAccounts?.length
            ? p.equityAccounts
            : p.equityAccount ? [p.equityAccount] : [];
        const prevOptions = prevAccounts
            .map((a) => this.findDropdownOption(a.accountNumber))
            .filter((o): o is IAccount & { displayLabel: string } => o != null);
        if (prevOptions.length > 0) {
            this.equityAccountControl.setValue(prevOptions, { emitEvent: false });
            this.selectedEquityAccounts = prevOptions.map(
                (o) => this.equityAccounts?.find((a) => a.accountNumber === o.accountNumber) ?? o,
            );
        }

        this.refreshBalanceValues();
        this.recalcDiffs();
        this.cdr.markForCheck();

        if (this.showFootnote) {
            setTimeout(() => this.ensureTiptapEditor(), 0);
        }
    }

    // ─── Output mapping ────────────────────────────────────────────────────────

    private buildOutput(): IAopCcrPersisted {
        const rows: ICcrEntryRow[] = this.entriesArray.value ?? this.entriesData;
        const entries: IAppropriationOfProfitEntry[] = rows.map((r, i) => ({
            name: r.name ?? '',
            currentValue: r.currentValue != null && NumberUtils.isFinite(r.currentValue)
                ? NumberUtils.roundNumber(r.currentValue) : null,
            previousValue: r.previousValue != null && NumberUtils.isFinite(r.previousValue)
                ? NumberUtils.roundNumber(r.previousValue) : null,
            // Persist the flag so that after save/reload we know which rows were
            // rolled forward from the prior year and should stay locked.
            carriedForwardFromPreviousYear: this.entriesData[i]?.fromPreviousYear === true || undefined,
        }));

        // Persist manual previous-year balance overrides only when the equity-account
        // data does not supply them (i.e. when openingPrev / closingPrev are null).
        const manualOpeningPrev = this.openingPrev == null && this.openingPrevControl.value != null
            ? +this.openingPrevControl.value : undefined;
        const manualClosingPrev = this.closingPrev == null && this.closingPrevControl.value != null
            ? +this.closingPrevControl.value : undefined;

        return {
            ccrEntries: entries,
            equityAccounts: this.selectedEquityAccounts.length > 0 ? this.selectedEquityAccounts : undefined,
            totalCapitalContributionsAtTheBeginningOfTheYearText: this.totalBeginningText,
            totalCapitalContributionsAtTheEndOfTheYearText: this.totalEndText,
            ccrDescription: this.ccrDescription,
            ccrFootnote: this.ccrFootnote || undefined,
            manualPreviousOpeningBalance: manualOpeningPrev,
            manualPreviousClosingBalance: manualClosingPrev,
        };
    }

    private emitOutput(): void {
        this.outputData = cloneDeep(this.buildOutput());
        this.ccrOutput.emit(this.outputData);
    }

    // ─── Helpers ───────────────────────────────────────────────────────────────

    private getDefaultDescription(): string {
        return this.i18next.t(this.local.aumico_annual_report_ccr_description, { lng: this.reportLocale }) as string;
    }

    private getDefaultBeginningText(): string {
        return this.i18next.t(this.local.aumico_annual_report_ccr_beginning_of_year, { lng: this.reportLocale }) as string;
    }

    private getDefaultEndText(): string {
        return this.i18next.t(this.local.aumico_annual_report_ccr_end_of_year, { lng: this.reportLocale }) as string;
    }
}
